/**
 * PRISM 五维扫描：硬编码内容 + analyze 响应解析 + 五维记录聚合。
 * 将后端 JSON（多种字段命名/嵌套）归一为 Step2 扫描流与 `DimensionScanContainer` 所需结构。
 */
import type {
  OnboardingChatMessageData,
  TDimensionKey,
  TDimensionRecord,
  TDimensionStatus,
  TFollowupDimensionConfig,
  TFollowupDimensionKey,
  TPrismAggregatedDimensionRecord,
  TPrismAnalyzePayload,
  TPrismReportMode,
  TPrismScanSessionSnapshot,
} from "@/types/onboarding-chat-message";

import { DIMENSION_ORDER } from "@/types/onboarding-chat-message";

import { isRecord } from "@/lib/utils";

type TPrismFiredTag = {
  id: string;
  dimension: TDimensionKey | "D5+D3";
  polarity: string;
  weight: number;
};

/** 单维扫描卡硬编码展示内容：维度名 + 叙述描述。 */
type TPrismDimensionHardcodedContent = {
  dimensionName: string;
  narrativeDescription: string;
};

/** 单维基础记录：仅含维度标识与硬编码内容，不含终态与标签。 */
type TPrismDimensionScanBaseRecord = {
  dimensionKey: TDimensionKey;
  dimensionName: string;
  narrativeDescription: string;
};

/** analyze `data_sources_hit` 单条结构化命中。 */
type TPrismDataSourceHit = {
  name: string;
  host: string;
  title: string;
  detail?: string;
};

/** Mode B 追问唯一配置源：field 与默认文案（选项仅来自 analyze 接口）。 */
const PRISM_FOLLOWUP_BY_DIMENSION = {
  D3: {
    field: "D3_community",
    title: "Community Reach",
    questionText: "How many members in your community channel?",
  },
  D4: {
    field: "D4_onchain",
    title: "On-chain Status",
    questionText: "What is your current onchain status?",
  },
  D5: {
    field: "D5_funding",
    title: "Capital",
    questionText: "What is your current funding stage?",
  },
} as const;

type TPrismQuestionField =
  (typeof PRISM_FOLLOWUP_BY_DIMENSION)[TFollowupDimensionKey]["field"];

const PRISM_QUESTION_FIELD_TO_DIMENSION_KEY = Object.fromEntries(
  (
    Object.entries(PRISM_FOLLOWUP_BY_DIMENSION) as [
      TFollowupDimensionKey,
      (typeof PRISM_FOLLOWUP_BY_DIMENSION)[TFollowupDimensionKey],
    ][]
  ).map(([dimensionKey, definition]) => [definition.field, dimensionKey]),
) as Record<TPrismQuestionField, TFollowupDimensionKey>;

const FOLLOWUP_DIMENSION_KEYS: readonly TFollowupDimensionKey[] = [
  "D3",
  "D4",
  "D5",
];

/** Mode B 追问 Skip：`POST .../prism/answer` 的 value，非 UI 选项。 */
export const PRISM_ANSWER_SKIP_VALUE = "Skip" as const;

/** 判断追问提交值是否为 Skip 哨兵（与 UI 选项区分）。 */
export function isPrismAnswerSkipValue(
  value: string,
): value is typeof PRISM_ANSWER_SKIP_VALUE {
  return value === PRISM_ANSWER_SKIP_VALUE;
}

/** 追问维度键 → `POST .../prism/answer` 的 field 名。 */
export function resolvePrismQuestionFieldFromFollowupDimensionKey(
  dimensionKey: TFollowupDimensionKey,
): TPrismQuestionField {
  return PRISM_FOLLOWUP_BY_DIMENSION[dimensionKey].field;
}

export type TPrismFollowupAnswerInput = {
  dimensionKey: TFollowupDimensionKey;
  selectedValue?: string;
  isSkipped?: boolean;
};

/** 将 UI 追问作答转为 answer 接口 batch 条目（Skip 映射为 `PRISM_ANSWER_SKIP_VALUE`）。 */
export function buildPrismFollowupAnswerEntries(
  answers: readonly TPrismFollowupAnswerInput[],
): Array<{ field: TPrismQuestionField; value: string }> {
  return answers.map(({ dimensionKey, selectedValue, isSkipped }) => {
    const field =
      resolvePrismQuestionFieldFromFollowupDimensionKey(dimensionKey);
    if (isSkipped) {
      return { field, value: PRISM_ANSWER_SKIP_VALUE };
    }
    if (!selectedValue) {
      throw new Error(`selectedValue is required for ${dimensionKey}`);
    }
    return { field, value: selectedValue };
  });
}

/** 返回单维追问卡的默认标题与题干（选项由 analyze 接口下发）。 */
export function getPrismFollowupDefaultDimensionConfig(
  dimensionKey: TFollowupDimensionKey,
): Pick<TFollowupDimensionConfig, "title" | "questionText"> {
  const definition = PRISM_FOLLOWUP_BY_DIMENSION[dimensionKey];
  return {
    title: definition.title,
    questionText: definition.questionText,
  };
}

/** 仅保留 analyze 已下发可选项的 D3–D5 维度（无 options 的不展示追问卡）。 */
export function filterFollowupDimensionKeysWithOptions(
  dimensionKeys: readonly TFollowupDimensionKey[],
  dimensionConfigsByKey?: Partial<
    Record<TFollowupDimensionKey, TFollowupDimensionConfig>
  >,
): TFollowupDimensionKey[] {
  return dimensionKeys.filter(
    (dimensionKey) =>
      (dimensionConfigsByKey?.[dimensionKey]?.options.length ?? 0) > 0,
  );
}

/** 模式 A/B（有信号）：五维叙述，与 prism-stream `default` streamMode 对齐。 */
const PRISM_DIMENSION_SCAN_MODE_AB_HARDCODED_CONTENT: Record<
  TDimensionKey,
  TPrismDimensionHardcodedContent
> = {
  D1: {
    dimensionName: "Project Profile",
    narrativeDescription: `I'm starting by getting clear on what your project actually is. Let me explore your project with X, RootData, and public docs together so I have a solid read on what you're building, what chain you're on, and what stage you're at.`,
  },
  D2: {
    dimensionName: "Market Attention",
    narrativeDescription: `I'm exploring how visible you are in the market right now — basically, is anyone actually paying attention. Visibility alone doesn't prove much, but its absence is usually a sign the story hasn't landed yet.`,
  },
  D3: {
    dimensionName: "Community Reach",
    narrativeDescription: `I'm looking at who's actually engaging with you — the composition of your audience and how it lines up with what you're building. This tells me about the makeup of the community you've gathered.`,
  },
  D4: {
    dimensionName: "Market Activity",
    narrativeDescription: `Now I'm checking the substance behind your project — whether your product is live in market, or if you're earlier stage, how active the build itself is. This gives me a read on real-world activity and build progress.`,
  },
  D5: {
    dimensionName: "Value Capture",
    narrativeDescription: `Now I'm working through how you plan to capture value — your token, your revenue model, and how it all flows together. This shows me your path to long-term value capture.`,
  },
};

// --- Hardcoded content builders ---

/** 批量生成五维基础记录（dimensionKey + dimensionName + narrativeDescription）。 */
function buildDimensionScanBaseRecords(
  handle?: string,
): TPrismDimensionScanBaseRecord[] {
  const trimmedHandle = handle?.trim() ?? "";
  const handleWithAt = trimmedHandle
    ? trimmedHandle.startsWith("@")
      ? trimmedHandle
      : `@${trimmedHandle}`
    : "";

  return DIMENSION_ORDER.map((dimensionKey) => {
    const hardcodedContent =
      PRISM_DIMENSION_SCAN_MODE_AB_HARDCODED_CONTENT[dimensionKey];
    return {
      dimensionKey,
      dimensionName: hardcodedContent.dimensionName,
      narrativeDescription: handleWithAt
        ? hardcodedContent.narrativeDescription.replace(
            /\byour project\b/i,
            handleWithAt,
          )
        : hardcodedContent.narrativeDescription,
    };
  });
}

// --- Analyze parsing ---

/**
 * 将单条原始 tag 对象规范为 `TPrismFiredTag`。
 * 纯字符串 tag 在按维 map 分支单独处理；此处遇到 string 返回 null。
 */
function normalizeFiredTag(rawTag: unknown): TPrismFiredTag | null {
  if (typeof rawTag === "string" && rawTag.trim()) {
    return null;
  }

  if (!isRecord(rawTag)) {
    return null;
  }

  const rawDimension = rawTag.dimension ?? rawTag.dim;
  let dimension: TPrismFiredTag["dimension"] | null = null;
  if (typeof rawDimension === "string") {
    const normalizedValue = rawDimension
      .trim()
      .toUpperCase()
      .replace(/_/g, "+");
    if (normalizedValue === "D5+D3") {
      dimension = "D5+D3";
    } else if (/^D[1-5]$/.test(normalizedValue)) {
      dimension = normalizedValue as TPrismFiredTag["dimension"];
    }
  }
  const tagId = String(
    rawTag.id ?? rawTag.tag_id ?? rawTag.label ?? rawTag.name ?? "",
  ).trim();

  if (!tagId || !dimension) {
    return null;
  }

  return {
    id: tagId,
    dimension,
    polarity: String(rawTag.polarity ?? ""),
    weight: typeof rawTag.weight === "number" ? rawTag.weight : 0,
  };
}

/**
 * 解开 Trex 常见响应包装，得到实际 analyze 载荷。
 * 优先 `obj`，其次 `data`，否则视为已是扁平载荷。
 */
function unwrapAnalyzePayload(
  analyzeResponse: TPrismAnalyzePayload,
): TPrismAnalyzePayload {
  if (isRecord(analyzeResponse.obj)) {
    return analyzeResponse.obj;
  }

  if (isRecord(analyzeResponse.data)) {
    return analyzeResponse.data;
  }

  return analyzeResponse;
}

type TPrismProfile = Record<string, unknown>;

type TDimensionProfileFieldSpec = {
  field: string;
  format: (value: unknown) => string | null;
};

function readTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** 从 profile 字段读取数值：支持 number 或带千分位逗号的字符串。 */
function readNumericProfileValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** 扫描卡计数紧凑格式 */
const COMPACT_COUNT_FORMATTER = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 0,
});

/** 扫描卡 USD 紧凑格式 */
const COMPACT_USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 0,
});

/** profile 计数字段 → `12M+ X followers` 类 dataTag。 */
function formatCountTag(value: unknown, suffix: string): string | null {
  const numeric = readNumericProfileValue(value);
  return numeric != null
    ? `${COMPACT_COUNT_FORMATTER.format(numeric)}+ ${suffix}`
    : null;
}

/** profile USD 字段 → `$12M TVL` 类 dataTag。 */
function formatUsdTag(value: unknown, label: string): string | null {
  const numeric = readNumericProfileValue(value);
  return numeric != null
    ? `${COMPACT_USD_FORMATTER.format(numeric)} ${label}`
    : null;
}

/**
 * profile 百分比字段 → `Popularity 12.5% ↑` 类 dataTag。
 * 后端可能传 0–1 小数或 0–100 百分数，统一按展示百分数处理。
 */
function formatPercentTrend(value: unknown, label: string): string | null {
  const numeric = readNumericProfileValue(value);
  if (numeric == null) {
    return null;
  }
  const percent = numeric <= 1 && numeric >= -1 ? numeric * 100 : numeric;
  const arrow = percent > 0 ? " ↑" : percent < 0 ? " ↓" : "";
  return `${label} ${Math.abs(percent)
    .toFixed(2)
    .replace(/\.?0+$/, "")}%${arrow}`;
}

/** 将 `defi_protocol` 等 snake_case 转为 `Defi Protocol` 展示文案。 */
function formatIdentityLabel(raw: string): string {
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(
      (part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`,
    )
    .join(" ");
}

/** 将 profile 字符串数组 join 为 dataTag；可映射单项，可选限制最多展示项数。 */
function formatStringArrayJoined(
  value: unknown,
  mapEntry: (entry: string) => string = (entry) => entry,
  maxEntries?: number,
): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const labels = value.flatMap((entry) =>
    typeof entry === "string" && entry.trim() ? [mapEntry(entry.trim())] : [],
  );
  const displayLabels =
    maxEntries != null ? labels.slice(0, maxEntries) : labels;
  return displayLabels.length > 0 ? displayLabels.join(" · ") : null;
}

/** D4 `token_symbol`：无 `$` 前缀时自动补上并大写 ticker。 */
function formatTokenSymbol(value: unknown): string | null {
  const token = readTrimmedString(value);
  if (!token) {
    return null;
  }
  return token.startsWith("$") ? token : `$${token.toUpperCase()}`;
}

/** D4 `holder_top10_pct`：0–1 或 0–100 均归一为 `Top 10: N%`。 */
function formatHolderTop10Pct(value: unknown): string | null {
  const holderPct = readNumericProfileValue(value);
  if (holderPct == null) {
    return null;
  }
  const normalized = holderPct <= 1 ? holderPct * 100 : holderPct;
  return `Top 10: ${Math.round(normalized)}%`;
}

/** D4 `listed_exchanges`：最多展示 3 家，格式 `Listed on a · b · c`。 */
function formatListedExchanges(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const exchanges = value.flatMap((entry) =>
    typeof entry === "string" && entry.trim() ? [entry.trim()] : [],
  );
  return exchanges.length > 0
    ? `Listed on ${exchanges.slice(0, 3).join(" · ")}`
    : null;
}

/** D5 `lead_backers`：最多 2 家，格式 `a16z Invested · Paradigm Invested`。 */
function formatLeadBackers(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const backers = value.flatMap((entry) =>
    typeof entry === "string" && entry.trim()
      ? [`${entry.trim()} Invested`]
      : [],
  );
  return backers.length > 0 ? backers.slice(0, 2).join(" · ") : null;
}

/** D5 `team_visibility`：`doxxed` / `anon` 映射为固定文案，其余按 identity 格式化。 */
function formatTeamVisibility(value: unknown): string | null {
  const visibility = readTrimmedString(value).toLowerCase();
  if (!visibility) {
    return null;
  }
  if (visibility === "doxxed") {
    return "Doxxed team";
  }
  if (visibility === "anon" || visibility === "anonymous") {
    return "Anonymous team";
  }
  return `${formatIdentityLabel(visibility)} team`;
}

/** D5 `audit_status`：`none` 不展示；已含 audited 则原样，否则补 `Audited by`。 */
function formatAuditStatus(value: unknown): string | null {
  const auditStatus = readTrimmedString(value);
  if (!auditStatus || auditStatus.toLowerCase() === "none") {
    return null;
  }
  return /audited/i.test(auditStatus)
    ? auditStatus
    : `Audited by ${auditStatus}`;
}

const PRISM_DIMENSION_PROFILE_FIELD_SPECS: Record<
  TDimensionKey,
  TDimensionProfileFieldSpec[]
> = {
  D1: [
    // 项目定位
    {
      field: "project_intro",
      format: (value) => readTrimmedString(value) || null,
    },
    // 项目正式名称
    {
      field: "project_name",
      format: (value) => readTrimmedString(value) || null,
    },
    // 项目赛道/身份
    {
      field: "project_identity",
      format: (value) => formatStringArrayJoined(value, formatIdentityLabel, 3),
    },
  ],
  D2: [
    // X 粉丝数
    {
      field: "twitter_followers",
      format: (value) => formatCountTag(value, "X followers"),
    },
    // RootData 项目热度分 (百分位 %)
    {
      field: "rootdata_popularity_score",
      format: (value) => formatPercentTrend(value, "Popularity"),
    },
    // 热度环比变化 %
    {
      field: "rootdata_popularity_growth",
      format: (value) => formatPercentTrend(value, "Growth"),
    },
    // CoinGecko 板块排名
    {
      field: "cg_category_rank",
      format: (value) => {
        const rank = readNumericProfileValue(value);
        return rank != null ? `CoinGecko #${Math.round(rank)}` : null;
      },
    },
  ],
  D3: [
    // Discord 成员数
    {
      field: "discord_members",
      format: (value) => formatCountTag(value, "Discord Members"),
    },
    // Telegram 群/频道订阅数
    {
      field: "tg_subscribers",
      format: (value) => formatCountTag(value, "TG Subscribers"),
    },
  ],
  D4: [
    // 所属链生态
    {
      field: "ecosystem",
      format: (value) => formatStringArrayJoined(value, formatIdentityLabel),
    },
    // token名称
    { field: "token_symbol", format: formatTokenSymbol },
    // 锁仓量 (USD)
    { field: "tvl_usd", format: (value) => formatUsdTag(value, "TVL") },
    // 7 日活跃用户数
    {
      field: "onchain_active_users_7d",
      format: (value) => {
        const numeric = readNumericProfileValue(value);
        return numeric != null
          ? `${COMPACT_COUNT_FORMATTER.format(numeric)} UAW/7d`
          : null;
      },
    },
    // 30 日协议手续费 (USD)
    {
      field: "fees_30d_usd",
      format: (value) => formatUsdTag(value, "fees/30d"),
    },
    // 30 日协议收入 (USD)
    {
      field: "revenue_30d_usd",
      format: (value) => formatUsdTag(value, "revenue/30d"),
    },
    // 市值 (USD)
    {
      field: "market_cap_usd",
      format: (value) => formatUsdTag(value, "Market Cap"),
    },
    // Top 10 持币集中度 %
    { field: "holder_top10_pct", format: formatHolderTop10Pct },
    // 已上交易所列表
    { field: "listed_exchanges", format: formatListedExchanges },
  ],
  D5: [
    // 总融资额 (USD)
    {
      field: "funding_total_usd",
      format: (value) => formatUsdTag(value, "Raised"),
    },
    // 领投/知名投资人
    { field: "lead_backers", format: formatLeadBackers },
    // 板块排名 (Top X%)
    {
      field: "sector_ranking",
      format: (value) => readTrimmedString(value) || null,
    },
    // 团队公开度 (doxxed/anon/mixed)
    { field: "team_visibility", format: formatTeamVisibility },
    // 审计状态 + 审计方
    { field: "audit_status", format: formatAuditStatus },
  ],
};

const D4_MAINNET_TOKEN_LIVE_FIELDS = new Set([
  "ecosystem",
  "token_symbol",
  "tvl_usd",
  "onchain_active_users_7d",
  "fees_30d_usd",
  "revenue_30d_usd",
  "market_cap_usd",
  "holder_top10_pct",
  "listed_exchanges",
]);

const D4_MAINNET_NO_TOKEN_FIELDS = new Set([
  "ecosystem",
  "tvl_usd",
  "onchain_active_users_7d",
  "fees_30d_usd",
  "revenue_30d_usd",
]);

/** D4 主网前阶段：testnet / concept / no_product 等不展示链上 dataTag。 */
function isPreMainnetProfile(profile: TPrismProfile): boolean {
  const onchainState = readTrimmedString(profile.onchain_state).toLowerCase();
  return (
    !onchainState ||
    onchainState === "testnet" ||
    onchainState === "concept" ||
    onchainState === "no_product" ||
    onchainState === "no product yet"
  );
}

/** D4 已上主网：`onchain_state` 为 mainnet 或 live。 */
function isMainnetLiveProfile(profile: TPrismProfile): boolean {
  const onchainState = readTrimmedString(profile.onchain_state).toLowerCase();
  return onchainState === "mainnet" || onchainState === "live";
}

/** D4 token 已发行：`token_state` 为 live 或 listed。 */
function isTokenLiveProfile(profile: TPrismProfile): boolean {
  const tokenState = readTrimmedString(profile.token_state).toLowerCase();
  return tokenState === "live" || tokenState === "listed";
}

/**
 * 按维 + profile 链上/token 状态过滤可展示的采集字段。
 * D4 主网无 token 时仅展示子集，并在 `buildDimensionDataTagsFromProfile` 追加 `Token planned`。
 */
function resolveDimensionProfileFieldSpecs(
  dimensionKey: TDimensionKey,
  profile: TPrismProfile,
): TDimensionProfileFieldSpec[] {
  if (dimensionKey !== "D4") {
    return PRISM_DIMENSION_PROFILE_FIELD_SPECS[dimensionKey];
  }

  if (isPreMainnetProfile(profile) || !isMainnetLiveProfile(profile)) {
    return [];
  }

  const allowedFields = isTokenLiveProfile(profile)
    ? D4_MAINNET_TOKEN_LIVE_FIELDS
    : D4_MAINNET_NO_TOKEN_FIELDS;

  return PRISM_DIMENSION_PROFILE_FIELD_SPECS.D4.filter((spec) =>
    allowedFields.has(spec.field),
  );
}

/** 从 analyze 多层嵌套（顶层 / report / inner report）定位 `profile` 对象。 */
function extractProfileFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): TPrismProfile | null {
  if (!analyzeResponse) {
    return null;
  }

  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);
  for (const candidate of [
    analyzePayload.profile,
    readNestedReport(analyzePayload)?.profile,
    readInnerReport(analyzePayload)?.profile,
  ]) {
    if (isRecord(candidate)) {
      return candidate;
    }
  }
  return null;
}

/**
 * 维内是否存在 neutral/positive fired tag（含 `D5+D3` 对 D3/D5 的跨维命中）。
 * 满足时才用 profile 字段生成 dataTag，否则不展示采集数据。
 */
function shouldShowProfileDataTagsForDimension(
  dimensionKey: TDimensionKey,
  firedTags: TPrismFiredTag[],
): boolean {
  return firedTags.some((tag) => {
    const polarity = tag.polarity.trim().toLowerCase();
    if (polarity !== "neutral" && polarity !== "positive") {
      return false;
    }
    return (
      tag.dimension === dimensionKey ||
      (tag.dimension === "D5+D3" &&
        (dimensionKey === "D3" || dimensionKey === "D5"))
    );
  });
}

/** 从 profile 提取单维 `dataTags`：维内存在 neutral/positive tag 时按采集表展示字段。 */
function buildDimensionDataTagsFromProfile(
  dimensionKey: TDimensionKey,
  profile: TPrismProfile | null,
  firedTags: TPrismFiredTag[],
): string[] {
  if (
    !profile ||
    !shouldShowProfileDataTagsForDimension(dimensionKey, firedTags)
  ) {
    return [];
  }

  const dataTags = resolveDimensionProfileFieldSpecs(
    dimensionKey,
    profile,
  ).flatMap((spec) => {
    const formatted = spec.format(profile[spec.field]);
    return formatted ? [formatted] : [];
  });

  if (
    dimensionKey === "D4" &&
    isMainnetLiveProfile(profile) &&
    !isTokenLiveProfile(profile) &&
    dataTags.length > 0
  ) {
    return [...dataTags, "Token planned"];
  }

  return dataTags;
}

/** 将 analyze 追问 `field`（如 `D3_community`）映射为维度键 D3–D5。 */
function resolveFollowupDimensionKeyFromQuestionField(
  rawField: unknown,
): TFollowupDimensionKey | null {
  if (typeof rawField !== "string" || !rawField.trim()) {
    return null;
  }

  const normalizedField = rawField.trim();
  if (normalizedField in PRISM_QUESTION_FIELD_TO_DIMENSION_KEY) {
    return PRISM_QUESTION_FIELD_TO_DIMENSION_KEY[
      normalizedField as TPrismQuestionField
    ];
  }

  return null;
}

/**
 * 展开 analyze 追问结构，支持：
 * - 顶层 `questions: [{ field, options }]`
 * - 包装对象 `{ type: "follow_up_questions", questions: [...] }`（见 analyze `question` 字段）
 */
function collectFollowUpQuestionEntries(candidate: unknown): unknown[] {
  if (candidate == null) {
    return [];
  }

  if (Array.isArray(candidate)) {
    return candidate.flatMap(collectFollowUpQuestionEntries);
  }

  if (typeof candidate === "string") {
    return [candidate];
  }

  if (!isRecord(candidate)) {
    return [];
  }

  if (Array.isArray(candidate.questions)) {
    return candidate.questions.flatMap(collectFollowUpQuestionEntries);
  }

  if (candidate.field != null) {
    return [candidate];
  }

  return [];
}

/**
 * 模式 B：从 analyze `questions` 推导需 `confirm_needed` 的维度（由 `field` 映射 D3–D5）。
 */
function extractConfirmNeededDimensionKeysFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): TFollowupDimensionKey[] {
  if (!analyzeResponse) {
    return [];
  }

  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);
  const confirmNeededDimensionKeys = new Set<TFollowupDimensionKey>();

  for (const candidate of collectFollowUpQuestionCandidates(analyzePayload)) {
    for (const questionItem of collectFollowUpQuestionEntries(candidate)) {
      if (typeof questionItem === "string") {
        const dimensionKey =
          resolveFollowupDimensionKeyFromQuestionField(questionItem);
        if (dimensionKey) {
          confirmNeededDimensionKeys.add(dimensionKey);
        }
        continue;
      }

      if (!isRecord(questionItem)) {
        continue;
      }

      const dimensionKey = resolveFollowupDimensionKeyFromQuestionField(
        questionItem.field,
      );
      if (dimensionKey) {
        confirmNeededDimensionKeys.add(dimensionKey);
      }
    }
  }

  return FOLLOWUP_DIMENSION_KEYS.flatMap((dimensionKey) =>
    confirmNeededDimensionKeys.has(dimensionKey) ? [dimensionKey] : [],
  );
}

/** 收集 analyze 各层可能承载追问列表的字段（questions / question / follow_up_questions）。 */
function collectFollowUpQuestionCandidates(
  analyzePayload: TPrismAnalyzePayload,
): unknown[] {
  return [
    analyzePayload.questions,
    analyzePayload.question,
    analyzePayload.follow_up_questions,
    readNestedReport(analyzePayload)?.questions,
    readNestedReport(analyzePayload)?.question,
    readNestedReport(analyzePayload)?.follow_up_questions,
  ];
}

/** 解析 analyze 下发的追问选项；无有效项时返回空数组。 */
function parseFollowupQuestionOptions(
  rawOptions: unknown,
): { value: string; label: string }[] {
  if (!Array.isArray(rawOptions)) {
    return [];
  }

  return rawOptions
    .map((item): { value: string; label: string } | null => {
      if (typeof item === "string") {
        const trimmed = item.trim();
        return trimmed ? { value: trimmed, label: trimmed } : null;
      }
      if (isRecord(item) && typeof item.value === "string") {
        const value = item.value.trim();
        if (!value) return null;
        const label =
          typeof item.label === "string" && item.label.trim()
            ? item.label.trim()
            : value;
        return { value, label };
      }
      return null;
    })
    .filter((item): item is { value: string; label: string } => item != null);
}

/**
 * 模式 B：从 analyze `questions` 推导各维追问展示配置（题干 / 标题 / 选项）。
 */
function extractFollowupDimensionConfigsFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): Partial<Record<TFollowupDimensionKey, TFollowupDimensionConfig>> {
  if (!analyzeResponse) {
    return {};
  }

  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);
  const dimensionConfigsByKey: Partial<
    Record<TFollowupDimensionKey, TFollowupDimensionConfig>
  > = {};
  for (const candidate of collectFollowUpQuestionCandidates(analyzePayload)) {
    for (const questionItem of collectFollowUpQuestionEntries(candidate)) {
      if (typeof questionItem === "string") {
        continue;
      }

      if (!isRecord(questionItem)) {
        continue;
      }

      const dimensionKey = resolveFollowupDimensionKeyFromQuestionField(
        questionItem.field,
      );
      if (!dimensionKey) {
        continue;
      }

      const options = parseFollowupQuestionOptions(questionItem.options);
      if (options.length === 0) {
        continue;
      }

      const defaults = getPrismFollowupDefaultDimensionConfig(dimensionKey);
      dimensionConfigsByKey[dimensionKey] = {
        title: defaults.title,
        questionText: defaults.questionText,
        options,
      };
    }
  }

  return dimensionConfigsByKey;
}

/** 读取 analyze 内嵌的外层 `report`（与 SSE `report` 事件结构对齐）。 */
function readNestedReport(
  analyzePayload: TPrismAnalyzePayload,
): Record<string, unknown> | null {
  const nestedReport = analyzePayload.report;
  if (isRecord(nestedReport)) {
    return nestedReport;
  }
  return null;
}

/** 读取外层 `report` 内的内层 `report`（summary / personas_top4 等落在此层）。 */
function readInnerReport(
  analyzePayload: TPrismAnalyzePayload,
): Record<string, unknown> | null {
  const outerReport = readNestedReport(analyzePayload);
  if (!outerReport) {
    return null;
  }
  const innerReport = outerReport.report;
  if (isRecord(innerReport)) {
    return innerReport;
  }
  return null;
}

/**
 * 从 `tags_by_dimension` / `tagsByDimension` 按 D1–D5 展开 fired tags。
 * 维内元素可为 tag 对象或 tag id 字符串。
 */
function extractFiredTagsFromDimensionMap(
  analyzePayload: TPrismAnalyzePayload,
): TPrismFiredTag[] {
  const dimensionMapCandidates: unknown[] = [
    analyzePayload.tags_by_dimension,
    analyzePayload.tagsByDimension,
    readNestedReport(analyzePayload)?.tags_by_dimension,
    readNestedReport(analyzePayload)?.tagsByDimension,
  ];

  for (const candidate of dimensionMapCandidates) {
    if (!isRecord(candidate)) {
      continue;
    }

    const mappedTags = DIMENSION_ORDER.flatMap((dimensionKey) => {
      const dimensionTags = candidate[dimensionKey];
      if (!Array.isArray(dimensionTags)) {
        return [];
      }

      return dimensionTags.flatMap((rawTag) => {
        if (typeof rawTag === "string" && rawTag.trim()) {
          return [
            {
              id: rawTag.trim(),
              dimension: dimensionKey,
              polarity: "",
              weight: 0,
            } satisfies TPrismFiredTag,
          ];
        }

        const firedTag = normalizeFiredTag(rawTag);
        return firedTag ? [firedTag] : [];
      });
    });

    if (mappedTags.length > 0) {
      return mappedTags;
    }
  }

  return [];
}

/**
 * 从 analyze 响应提取全部 fired tags。
 * 解析顺序：按维 map → 顶层/内嵌 `tags` / `fired_tags`（与 SSE report 字段对齐）。
 */
export function extractFiredTagsFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): TPrismFiredTag[] {
  if (!analyzeResponse) {
    return [];
  }

  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);

  const tagsFromDimensionMap = extractFiredTagsFromDimensionMap(analyzePayload);
  if (tagsFromDimensionMap.length > 0) {
    return tagsFromDimensionMap;
  }

  const tagCandidates: unknown[] = [
    analyzePayload.tags,
    analyzePayload.fired_tags,
    analyzePayload.firedTags,
    readNestedReport(analyzePayload)?.tags,
    readNestedReport(analyzePayload)?.fired_tags,
  ];

  for (const candidate of tagCandidates) {
    if (!Array.isArray(candidate)) {
      continue;
    }

    const normalizedTags = candidate.flatMap((rawTag) => {
      const firedTag = normalizeFiredTag(rawTag);
      return firedTag ? [firedTag] : [];
    });

    if (normalizedTags.length > 0) {
      return normalizedTags;
    }
  }

  return [];
}

/**
 * 提取 PRISM 报告模式 A/B/C。
 * 用于决定 D3–D5 是否进入 `confirm_needed`（模式 B 部分缺失）。
 */
function extractReportModeFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): TPrismReportMode | null {
  if (!analyzeResponse) {
    return null;
  }

  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);
  const rawMode =
    analyzePayload.mode ??
    analyzePayload.report_mode ??
    readNestedReport(analyzePayload)?.mode;

  if (typeof rawMode !== "string") {
    return null;
  }

  const normalizedMode = rawMode.trim().toUpperCase();
  if (
    normalizedMode === "A" ||
    normalizedMode === "B" ||
    normalizedMode === "C"
  ) {
    return normalizedMode;
  }

  return null;
}

/**
 * 将单条 `data_sources_hit` 归一为 Web Search 卡结构。
 * 兼容历史纯字符串（从文案中提取域名）与对象 `{ name, host, title, detail }`。
 */
function normalizeDataSourceHit(rawHit: unknown): TPrismDataSourceHit | null {
  if (typeof rawHit === "string" && rawHit.trim()) {
    const hitText = rawHit.trim();
    const domainMatch = hitText.match(/(?:^|\s)([\w.-]+\.[a-z]{2,})(?:\s|$)/i);
    const host = domainMatch?.[1] ?? hitText;
    return {
      name: host,
      host,
      title: hitText,
    };
  }

  if (!isRecord(rawHit)) {
    return null;
  }

  const title = String(rawHit.title ?? "").trim();
  const host = String(rawHit.host ?? rawHit.domain ?? "").trim();
  const name = String(rawHit.name ?? host).trim();
  const detail =
    typeof rawHit.detail === "string" && rawHit.detail.trim()
      ? rawHit.detail.trim()
      : undefined;

  if (!title && !host && !name) {
    return null;
  }

  const resolvedHost = host || name || title;
  const resolvedTitle = title || name || host;

  return {
    name: name || resolvedHost,
    host: resolvedHost,
    title: resolvedTitle,
    ...(detail ? { detail } : {}),
  };
}

/**
 * 提取本次扫描命中的数据源列表（用于 Web Search 结果卡展示）。
 * 支持 analyze `data_sources_hit` 对象数组（`name` / `host` / `title` / `detail`）及历史字符串格式。
 */
function extractDataSourcesHitFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): TPrismDataSourceHit[] {
  if (!analyzeResponse) {
    return [];
  }

  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);
  const hitCandidates: unknown[] = [
    analyzePayload.data_sources_hit,
    readNestedReport(analyzePayload)?.data_sources_hit,
  ];

  for (const candidate of hitCandidates) {
    if (!Array.isArray(candidate)) {
      continue;
    }

    const normalizedHits = candidate.flatMap((hit) => {
      const normalizedHit = normalizeDataSourceHit(hit);
      return normalizedHit ? [normalizedHit] : [];
    });

    if (normalizedHits.length > 0) {
      return normalizedHits;
    }
  }

  return [];
}

/**
 * 是否按模式 C（无信号 / 兜底）处理五维终态。
 * `streamMode === "no_signal"` 或 analyze 返回 `mode: C` 时成立。
 */
function resolveIsModeCOutcome(
  reportMode: TPrismReportMode | null,
  streamMode: "default" | "no_signal",
): boolean {
  return streamMode === "no_signal" || reportMode === "C";
}

/**
 * 计算单维在扫描流结束时的 `finalStatus`。
 * - 模式 C：五维均为 `confirm_needed`
 * - 模式 B：仅 analyze `questions` 中出现的 field 对应维度为 `confirm_needed`
 * - 模式 A：五维均为 `done`
 */
function resolveDimensionFinalStatus(
  dimensionKey: TDimensionKey,
  reportMode: TPrismReportMode | null,
  isModeCOutcome: boolean,
  modeBConfirmNeededDimensionKeys: ReadonlySet<TFollowupDimensionKey>,
): TDimensionStatus {
  if (isModeCOutcome) {
    return "confirm_needed";
  }

  if (reportMode === "B") {
    return modeBConfirmNeededDimensionKeys.has(
      dimensionKey as TFollowupDimensionKey,
    )
      ? "confirm_needed"
      : "done";
  }

  return "done";
}

// --- Aggregation ---

/**
 * 将 analyze 响应聚合为五维扫描记录（BFF 写入 UIMessage `data-dimension`）。
 * - dimensionName / narrativeDescription：硬编码内容 + handle 个性化
 * - dataTags：维内存在 neutral/positive tag 时，从 profile 按采集表字段格式化展示
 * - finalStatus：模式 B 由 analyze `questions` field 推导，A/C 逻辑不变
 */
function buildDimensionScanRecordsFromAnalyze(input: {
  analyzeResponse: TPrismAnalyzePayload | null;
  streamMode: "default" | "no_signal";
  handle?: string;
}): TPrismAggregatedDimensionRecord[] {
  const firedTags = extractFiredTagsFromAnalyze(input.analyzeResponse);
  const profile = extractProfileFromAnalyze(input.analyzeResponse);
  const modeBConfirmNeededDimensionKeys = new Set(
    extractConfirmNeededDimensionKeysFromAnalyze(input.analyzeResponse),
  );
  let reportMode = extractReportModeFromAnalyze(input.analyzeResponse);
  if (
    reportMode == null &&
    modeBConfirmNeededDimensionKeys.size > 0 &&
    input.streamMode !== "no_signal"
  ) {
    reportMode = "B";
  }
  const isModeCOutcome = resolveIsModeCOutcome(reportMode, input.streamMode);
  const baseRecords = buildDimensionScanBaseRecords(input.handle);

  return baseRecords.map((record) => {
    const finalStatus = resolveDimensionFinalStatus(
      record.dimensionKey,
      reportMode,
      isModeCOutcome,
      modeBConfirmNeededDimensionKeys,
    );
    const dataTags = buildDimensionDataTagsFromProfile(
      record.dimensionKey,
      profile,
      firedTags,
    );

    return {
      ...record,
      ...(dataTags.length > 0 ? { dataTags } : {}),
      finalStatus,
    };
  });
}

/** 将 analyze `data_sources_hit` 转为 `WebSearchCard` 所需的 `TWebSearchSource[]`。 */
function buildWebSearchSourcesFromDataSourcesHit(
  dataSourcesHit: readonly TPrismDataSourceHit[],
): Array<{
  title: string;
  domain: string;
  name?: string;
  detail?: string;
}> {
  return dataSourcesHit.map((hit) => ({
    title: hit.title,
    domain: hit.host,
    name: hit.name,
    ...(hit.detail ? { detail: hit.detail } : {}),
  }));
}

/** 由五维终态 status 反推 PRISM 报告模式（草稿缓存恢复用）。 */
export function inferReportModeFromDimensionRecords(
  dimensions: readonly Pick<TDimensionRecord, "dimensionKey" | "status">[],
): TPrismReportMode {
  const confirmNeededCount = dimensions.filter(
    (dimension) => dimension.status === "confirm_needed",
  ).length;

  if (confirmNeededCount === 5) {
    return "C";
  }

  if (confirmNeededCount > 0) {
    return "B";
  }

  return "A";
}

/** 由草稿缓存的模式 + tag 重建五维扫描记录（硬编码文案 + 无动画回放）。 */
export function buildDimensionScanRecordsFromDraftCache(input: {
  reportMode: TPrismReportMode;
  tagsByDimension: Partial<Record<TDimensionKey, string[]>>;
  handle?: string;
  confirmNeededDimensionKeys?: readonly TFollowupDimensionKey[];
}): TPrismAggregatedDimensionRecord[] {
  const isModeCOutcome = input.reportMode === "C";
  const modeBConfirmNeededDimensionKeys = new Set(
    input.confirmNeededDimensionKeys ?? [],
  );
  const baseRecords = buildDimensionScanBaseRecords(input.handle);

  return baseRecords.map((record) => {
    const finalStatus = resolveDimensionFinalStatus(
      record.dimensionKey,
      input.reportMode === "C" ? null : input.reportMode,
      isModeCOutcome,
      modeBConfirmNeededDimensionKeys,
    );
    const cachedTags = input.tagsByDimension[record.dimensionKey];

    return {
      ...record,
      ...(cachedTags && cachedTags.length > 0 ? { dataTags: cachedTags } : {}),
      finalStatus,
    };
  });
}

/** 从 analyze / report 载荷读取 `personas_top4`，归一为 persona code 列表。 */
function extractPersonasTop4CodesFromAnalyze(
  analyzeResponse: TPrismAnalyzePayload | null,
): string[] {
  if (!analyzeResponse) {
    return [];
  }
  const analyzePayload = unwrapAnalyzePayload(analyzeResponse);
  const top4Candidates: unknown[] = [
    analyzePayload.personas_top4,
    readInnerReport(analyzePayload)?.personas_top4,
    readNestedReport(analyzePayload)?.personas_top4,
  ];
  for (const candidate of top4Candidates) {
    if (!Array.isArray(candidate)) {
      continue;
    }

    const codes = candidate.flatMap((entry) => {
      if (typeof entry === "string" && entry.trim()) {
        return [entry.trim()];
      }
      if (!isRecord(entry)) {
        return [];
      }
      const personaCode = String(
        entry.persona_id ??
          entry.personaId ??
          entry.audience_segment_code ??
          entry.code ??
          "",
      ).trim();
      return personaCode ? [personaCode] : [];
    });
    if (codes.length > 0) {
      return codes;
    }
  }

  return [];
}

/**
 * prism-stream 流式写出时序配置（Web Search + 五维扫描）。
 * `webSearch.default.rowDelayMs`（110）应与 WebSearchCard `LIST_STAGGER_ROW_DELAY_MS` 一致。
 */
const PRISM_STREAM_TIMING_CONFIG = {
  webSearch: {
    /** 与前端 `parseScanStream` 约定的 Web Search data part id。 */
    dataPartId: "web-search-data-source",
    default: {
      rowDelayMs: 110,
    },
    noSignal: {
      rowDelayMs: 180,
    },
  },
  dimension: {
    revealDelayMs: 600,
    doneDelayMs: 400,
  },
} as const;

/** 解析 report / 扫描步骤使用的 X handle：project → snapshot.scanHandle → 页面输入。 */
function resolveScanHandleFromMessageData(
  messageData: OnboardingChatMessageData | undefined,
  fallbackProjectInput: string,
): string {
  const fromProject = String(messageData?.project ?? "")
    .trim()
    .replace(/^@+/, "");
  if (fromProject) {
    return fromProject;
  }

  const scanSnapshot = messageData?.prismScanSnapshot as
    | TPrismScanSessionSnapshot
    | undefined;
  const fromSnapshot = String(scanSnapshot?.scanHandle ?? "")
    .trim()
    .replace(/^@+/, "");
  if (fromSnapshot) {
    return fromSnapshot;
  }

  return fallbackProjectInput.trim().replace(/^@+/, "");
}

/** 从聊天消息 `data.prismScanSnapshot` 统计已完成维度数（与 StepScanPanel 中展示done的维度数一致）。 */
function resolveDimensionCountFromScanSnapshot(
  messageData: OnboardingChatMessageData | undefined,
): number {
  const scanSnapshot = messageData?.prismScanSnapshot as
    | TPrismScanSessionSnapshot
    | undefined;
  const scanDimensions = scanSnapshot?.dimensions ?? [];
  if (scanDimensions.length === 0) {
    return 0;
  }

  const completedDimensionCount = scanDimensions.filter((dimension) =>
    Boolean(dimension.status),
  ).length;
  return completedDimensionCount > 0
    ? completedDimensionCount
    : scanDimensions.length;
}

// --- Exports ---

export type { TPrismQuestionField };

export {
  PRISM_STREAM_TIMING_CONFIG,
  unwrapAnalyzePayload,
  extractReportModeFromAnalyze,
  extractConfirmNeededDimensionKeysFromAnalyze,
  extractFollowupDimensionConfigsFromAnalyze,
  extractDataSourcesHitFromAnalyze,
  buildDimensionScanRecordsFromAnalyze,
  buildWebSearchSourcesFromDataSourcesHit,
  extractPersonasTop4CodesFromAnalyze,
  resolveScanHandleFromMessageData,
  resolveDimensionCountFromScanSnapshot,
};
